import { WorkspacePermissionSubjectType, WorkspaceResourceFolderType, WorkspaceResourceType, WorkspaceUserGroupType, WorkspaceUserRole } from "common/interfaces";
import { presentationLinkUserEmailRegex } from "common/constants";
import PresentationPermissionsController from "js/controllers/PresentationPermissionsController";
import WorkspaceController from "js/controllers/WorkspaceController";
import { delay } from "js/core/utilities/promiseHelper";
import { emailRegex } from "common/regex";
import { app } from "js/namespaces";
import { _ } from "js/vendor";
import { t } from "js/i18n";

/**
 * Returns invite suggestions in the shape that is compatible with the old invite context, has to do weird stuff because of that...
 */
async function getAllInviteSuggestions(presentationId, includeFolders, includeGroups, includeGroupTypes, includeCurrentUser, includeUsersWithRoles) {
    let suggestions = [];

    if (!WorkspaceController.initialized) {
        return suggestions;
    }

    if (presentationId) {
        await PresentationPermissionsController.waitForAsyncActions();

        if (!PresentationPermissionsController.initialized || PresentationPermissionsController.presentationId !== presentationId || PresentationPermissionsController.workspaceId !== WorkspaceController.workspaceId) {
            await PresentationPermissionsController.initialize(presentationId, WorkspaceController.workspaceId);
        }

        suggestions.push(...PresentationPermissionsController.invites.map(invite => ({
            type: "user",
            id: invite.inviteeUid,
            uid: invite.inviteeUid,
            email: invite.inviteeEmail,
            displayName: invite.inviteeEmail,
            permissionType: invite.permissions.write ? "edit" : "view",
            photoURL: null,
            isPending: true,
            isCollaborator: true
        })));

        WorkspaceController.users.forEach(user => {
            const permission = PresentationPermissionsController.permissions.find(p => p.subjectId === user.uid && p.subjectType === WorkspacePermissionSubjectType.USER);
            if (permission) {
                suggestions.push({
                    type: "user",
                    id: user.uid,
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    permissionType: permission.owner ? "owner" : permission.write.object ? "edit" : "view",
                    photoURL: user.photoURL,
                    isPending: false,
                    isCollaborator: true,
                    isGuest: user.role === WorkspaceUserRole.GUEST,
                    isInTeam: WorkspaceController.users.some(u => u.uid === user.uid)
                });
            }
        });
    }

    suggestions.push(...WorkspaceController.users
        .filter(user => !suggestions.some(s => s.uid === user.uid))
        .map(user => ({
            type: "user",
            id: user.uid,
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            isCollaborator: false,
            isPending: false,
            isInTeam: true,
            isGuest: user.role === WorkspaceUserRole.GUEST
        })));

    if (!includeCurrentUser) {
        suggestions = suggestions.filter(s => s.uid !== app.user.id);
    }

    if (includeGroups && WorkspaceController.isMultiUserWorkspace) {
        suggestions.push(...WorkspaceController.userGroups
            .filter(group => includeGroupTypes.includes(group.type))
            .map(group => {
                const permission = PresentationPermissionsController.permissions.find(p => p.subjectId === group.id && p.subjectType === WorkspacePermissionSubjectType.USER_GROUP);
                return {
                    type: "group",
                    id: group.id,
                    groupType: group.type,
                    name: group.name,
                    users: WorkspaceController.getUsersInGroup(group.id).map(user => ({
                        type: "user",
                        id: user.uid,
                        uid: user.uid,
                        email: user.email,
                        displayName: user.displayName,
                        photoURL: user.photoURL,
                        isGuest: user.role === WorkspaceUserRole.GUEST,
                        isInTeam: true
                    })),
                    permissionType: permission ? (permission.write.object ? "edit" : "view") : undefined
                };
            }));
    }

    if (includeFolders) {
        suggestions.push(...WorkspaceController.resourceFolders
            .filter(f => f.folderType !== WorkspaceResourceFolderType.PERSONAL && f.resourceTypes.includes(WorkspaceResourceType.PRESENTATION))
            .map(folder => {
                const users = WorkspaceController.getUsersWithAccessToResourceFolder(folder.id);
                return {
                    type: "folder",
                    id: folder.id,
                    name: folder.name,
                    isDefault: folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT,
                    users: users.map(user => ({
                        isInTeam: true,
                        id: user.uid,
                        uid: user.uid,
                        email: user.email,
                        displayName: user.displayName,
                        isGuest: user.role === WorkspaceUserRole.GUEST
                    }))
                };
            })
        );
    }

    if (includeUsersWithRoles) {
        suggestions = suggestions.filter(s => s.type === "user");
        suggestions = suggestions.filter(s => !s.isPending);
        suggestions = suggestions.filter(s => {
            const user = WorkspaceController.users.find(u => u.uid === s.uid);
            return includeUsersWithRoles.includes(user?.role);
        });
    }

    return suggestions;
}

const getDefaultFilterPlaceholder = ({ includeFolders, includeGroups, allowEmailInvites }) => {
    return [
        includeFolders && "#team folder",
        "@teammate",
        includeGroups && "#group",
        allowEmailInvites && "or type email",
    ].filter(Boolean).join(", ");
};

export const createInviteSuggestionContext = options => {
    let {
        includeCurrentUser = false,
        includeFolders = false,
        includeGroups = false,
        includeGroupTypes = [WorkspaceUserGroupType.GUESTS, WorkspaceUserGroupType.CUSTOM],
        includeUsersWithRoles = null,
        filterPlaceholder,
        labelCategoryOld = "Presentation Collaborators",
        labelCategoryNew = "Suggestions",
        allSuggestions: defaultAllSuggestions = [],
        oldSuggestions: defaultOldSuggestions = [],
        selection: defaultSelection = [],
        existingAccessItems = [],
        oldFirst = false,
        hideOld = false,
        prohibitExternalWorkspaceCollaboration = false,
        allowEmailInvites = true,
        saveContext,
        afterInit,
        presentationId,
    } = options;

    filterPlaceholder = filterPlaceholder || getDefaultFilterPlaceholder({ includeFolders, includeGroups, allowEmailInvites });

    const context = {
        allSuggestions: [],
        newSuggestions: [],
        oldSuggestions: [],
        filteredAllSuggestions: [],
        filteredNewSuggestions: [],
        filteredOldSuggestions: [],
        selectableSuggestions: [],
        selection: [],
        filter: "",
        filterPlaceholder,
        labelCategoryOld,
        labelCategoryNew,
        oldFirst,
        pendingSelectionIndex: 0,
    };

    const isCurrentUserItem = user => {
        if (user.uid) {
            return user.uid === app.user.id;
        }
        return !!user.email && user.email === app.user?.get("email");
    };

    const upgradeEmailsToSuggestions = items => {
        const {
            allSuggestions,
        } = context;

        items = items.map(item => {
            // Convert the string items to an email item
            if (_.isString(item)) {
                // Remove type filters
                if (item.startsWith("@") || item.startsWith("#")) {
                    item = item.slice(1);
                }
                item = {
                    type: "email",
                    email: item,
                };
            }

            // Folder items don't have an email property.
            //   Otherwise, ensure emails are lowercase
            if (item.email) {
                item.email = item.email.toLowerCase();
            }

            if (!item.type) {
                if (item.email) {
                    item.type = item.id ? "user" : "email";
                } else if (item.users) {
                    item.type = "folder";
                }
            }

            // Convert email items to any existing user items
            if (item.type === "email") {
                // Convert the email item to an existing user if possible
                let user = allSuggestions.find(x => x.email === item.email);
                if (user) {
                    return user;
                }
            }

            return item;
        });

        return items;
    };

    const sortSuggestions = () => {
        const sortFunc = (a, b) => {
            const nameA = (a.displayName || a.name || a.email)?.toLowerCase();
            const nameB = (b.displayName || b.name || b.email)?.toLowerCase();

            return nameA < nameB ? -1 : 1;
        };

        context.oldSuggestions = upgradeEmailsToSuggestions(context.oldSuggestions)
            .sort(sortFunc);

        context.newSuggestions = context.allSuggestions
            .filter(item =>
                !context.oldSuggestions.some(x => (
                    (
                        x.id &&
                        x.id === item.id
                    ) ||
                    (
                        x.email &&
                        x.email === item.email
                    )
                ))
            )
            .sort(sortFunc);

        if (oldFirst) {
            context.allSuggestions = [
                ...context.oldSuggestions,
                ...context.newSuggestions,
            ];
        } else {
            context.allSuggestions = [
                ...context.newSuggestions,
                ...context.oldSuggestions,
            ];
        }
    };

    const withGuestFlag = item => {
        if (!item || item.type === "folder" || item.type === "group") {
            return item;
        }

        // Prefer role directly on the item if provided
        let role = item.role;

        // Otherwise, lookup the user by uid/id or email in the workspace
        if (!role) {
            const uid = item.uid || item.id;
            const user = uid
                ? WorkspaceController.users.find(u => u.uid === uid)
                : (item.email ? WorkspaceController.users.find(u => u.email === item.email) : null);
            role = user?.role;
        }

        if (role === undefined) {
            return item;
        }

        return {
            ...item,
            isGuest: role === WorkspaceUserRole.GUEST
        };
    };

    const filterSuggestions = () => {
        let {
            newSuggestions,
            oldSuggestions,
            selection,
            filter,
        } = context;

        context.filteredAllSuggestions = [];
        context.filteredNewSuggestions = [];
        context.filteredOldSuggestions = [];

        // Item type filtering
        let searchUsers = true;
        let searchFolders = includeFolders;
        let searchGroups = includeGroups;
        if (filter.startsWith("@")) {
            searchFolders = false;
            searchGroups = false;
            filter = filter.slice(1);
        } else if (filter.startsWith("#")) {
            searchUsers = false;
            searchFolders = false;
            filter = filter.slice(1);
        }

        const includeItem = item => (
            (
                (
                    searchUsers &&
                    item.type !== "folder" &&
                    item.type !== "group"
                ) ||
                (
                    searchFolders &&
                    item.type === "folder"
                ) ||
                (
                    searchGroups &&
                    item.type === "group"
                )
            ) &&
            (
                item.displayName?.toLowerCase().includes(filter) ||
                item.name?.toLowerCase().includes(filter) ||
                item.email?.toLowerCase().includes(filter)
            )
        );

        context.filteredNewSuggestions = newSuggestions.filter(item => (
            !selection.some(x => x.id === item.id) &&
            includeItem(item)
        ));

        if (!hideOld) {
            context.filteredOldSuggestions = oldSuggestions.filter(item => (
                !selection.some(x => x.id === item.id) &&
                includeItem(item)
            ));
        }

        if (oldFirst) {
            context.filteredAllSuggestions = [
                ...context.filteredOldSuggestions,
                ...context.filteredNewSuggestions,
            ];
        } else {
            context.filteredAllSuggestions = [
                ...context.filteredNewSuggestions,
                ...context.filteredOldSuggestions,
            ];
        }

        context.selectableSuggestions = context.filteredAllSuggestions;

        if (context.pendingSelectionIndex >= context.selectableSuggestions.length) {
            context.pendingSelectionIndex = Math.max(context.selectableSuggestions.length - 1, 0);
        }
    };

    // Ensure defaults are in the correct form
    defaultAllSuggestions = upgradeEmailsToSuggestions(defaultAllSuggestions);
    defaultSelection = upgradeEmailsToSuggestions(defaultSelection);
    defaultOldSuggestions = upgradeEmailsToSuggestions(defaultOldSuggestions);

    context.init = async () => {
        // Reset suggestions
        context.allSuggestions = [...defaultAllSuggestions];
        context.oldSuggestions = [...defaultOldSuggestions];

        // Clear to defaults
        context.selection = [...defaultSelection];

        if (!context.allSuggestions?.length) {
            context.allSuggestions = await getAllInviteSuggestions(presentationId, includeFolders, includeGroups, includeGroupTypes, includeCurrentUser, includeUsersWithRoles);
        }

        // Ensure guest flags exist for rendering badges
        // Temporary, filter out old presentation link users in the UI until we clean them up
        context.allSuggestions = context.allSuggestions.map(withGuestFlag).filter(item => !item.email?.match(presentationLinkUserEmailRegex));
        context.oldSuggestions = context.oldSuggestions.map(withGuestFlag).filter(item => !item.email?.match(presentationLinkUserEmailRegex));
        context.selection = context.selection.map(withGuestFlag).filter(item => !item.email?.match(presentationLinkUserEmailRegex));

        if (!context.oldSuggestions?.length) {
            // Default the old suggestions to an empty array
            context.oldSuggestions = [];

            // Suggestions with permissions are collaborators
            context.oldSuggestions = context.allSuggestions.filter(item => !!item.permissionType);
        }

        sortSuggestions();

        filterSuggestions();
        saveContext(context);

        if (afterInit) {
            await delay(10);
            afterInit();
        }
    };

    context.getFilterWithoutCategories = () => {
        let {
            filter,
        } = context;

        if (filter.startsWith("@") || filter.startsWith("#")) {
            return filter.slice(1);
        }
        return filter;
    };

    context.clearSelection = () => {
        context.selection = [...defaultSelection];
        context.pendingSelectionIndex = 0;
        filterSuggestions();

        saveContext(context);
    };

    context.selectItems = items => {
        const {
            selection,
            allSuggestions,
        } = context;

        const errors = [];

        const oldLength = selection.length;

        // Remove the error on add
        context.errors = null;

        const matchesItem = (a, b) => (
            (
                a.id &&
                a.id === b.id
            ) ||
            (
                a.uid &&
                a.uid === b.uid
            ) ||
            (
                a.email &&
                a.email === b.email
            )
        );

        const itemAlreadyHasAccess = item => {
            if (!includeCurrentUser && isCurrentUserItem(item)) {
                return true;
            }

            return existingAccessItems.some(existing => matchesItem(existing, item));
        };

        const isExistingSuggestion = item => allSuggestions.some(x => matchesItem(x, item));

        const isUnprohibited = item => {
            if (isExistingSuggestion(item)) {
                return true;
            }

            // Member pickers only add existing workspace users.
            if (!allowEmailInvites) {
                errors.push(t("No matches"));
                return false;
            }

            // Presentation sharing: unknown emails are guest invites, blocked by workspace setting.
            if (prohibitExternalWorkspaceCollaboration) {
                errors.push(`The Team Owner has disabled collaboration outside of this workspace.`);
                return false;
            }

            return true;
        };

        const selectUnselected = item => {
            const isSelected = selection.some(x => matchesItem(x, item));

            if (isSelected) {
                selection.push({ ...item, inviteError: "duplicate" });
                return;
            }

            if (itemAlreadyHasAccess(item)) {
                selection.push({ ...item, inviteError: "alreadyHasAccess" });
                return;
            }

            selection.push(item);
        };

        items = upgradeEmailsToSuggestions(items);

        items.forEach(item => {
            if (
                item.type === "folder" ||
                item.type === "group" ||
                item.email?.match(emailRegex)
            ) {
                if (isUnprohibited(item)) {
                    // Normalize item so guest badge can render
                    selectUnselected(withGuestFlag(item));
                }
                // If the email failed validation and is non-empty, report a format error
            } else if (item.email) {
                errors.push(`Emails must be in the form of "email@domain.xyz"`);
            }
        });

        // If we've added items, save
        if (oldLength !== selection.length) {
            context.filter = "";
            context.pendingSelectionIndex = 0;
            filterSuggestions();
            saveContext(context);
        }

        // Process after a delay to prevent errors being removed by sequential context calls
        if (errors.length) {
            setTimeout(() => {
                context.errors = _.uniq(errors);
                saveContext(context);
            }, 10);
        }
    };

    context.deselectItems = items => {
        const {
            selection,
        } = context;

        const oldLength = selection.length;

        items.forEach(item => {
            const index = selection.findIndex(x => (
                x === item ||
                x.id === item.id ||
                x.email === item.email
            ));

            if (index > -1) {
                selection.splice(index, 1);
            }
        });

        if (oldLength !== selection.length) {
            // Remove the format error on remove
            context.errors = null;
            filterSuggestions();
            saveContext(context);
        }
    };

    context.popItem = () => {
        const item = context.selection.pop();
        if (item) {
            // Remove the format error on remove
            context.errors = null;
            filterSuggestions();
            saveContext(context);
        }
        return item;
    };

    context.setFilter = filter => {
        filter = filter.toLowerCase();
        if (context.filter !== filter) {
            // If the filter has changed or is empty, remove the format error
            context.errors = null;
            context.filter = filter;
            // Reset the selection index every time the filter changes
            context.pendingSelectionIndex = 0;
            filterSuggestions();
            saveContext(context);
        }
    };

    context.selectFilterAsEmail = () => {
        const filter = context.filter;
        if (!filter?.length) {
            return;
        }

        // Only commit typed text as an email when it looks like one.
        // Otherwise show "No matches" on blur/commit instead of a format error
        // while the user is still searching.
        if (!filter.match(emailRegex)) {
            if (!context.filteredAllSuggestions.length) {
                context.errors = [t("No matches")];
                saveContext(context);
            }
            return;
        }

        context.selectItems([filter]);
        saveContext(context);
    };

    context.setPendingSelectionIndex = index => {
        if (index < 0) {
            index = Math.max(context.selectableSuggestions.length - 1, 0);
        } else if (index >= context.selectableSuggestions.length) {
            index = 0;
        }
        context.pendingSelectionIndex = index;
        saveContext(context);
    };

    context.pendingSelectPrev = () =>
        context.setPendingSelectionIndex(context.pendingSelectionIndex - 1);
    context.pendingSelectNext = () =>
        context.setPendingSelectionIndex(context.pendingSelectionIndex + 1);
    context.selectPendingSelection = () => {
        if (!context.selectableSuggestions.length) {
            return null;
        }

        const item = context.selectableSuggestions[context.pendingSelectionIndex];
        context.selectItems([item]);
        return item;
    };

    context.getSelectedUniqueUsers = () => {
        const { selection } = context;

        let users = selection
            .filter(item => !item.inviteError)
            .filter(item => item.type === "folder" || item.type === "group" || includeCurrentUser || !isCurrentUserItem(item))
            .map(item => {
                switch (item.type) {
                    case "user":
                        return item;
                    case "folder":
                    case "group":
                        return item.users.filter(user => includeCurrentUser || !isCurrentUserItem(user));
                    case "email":
                    default:
                        return {
                            id: null,
                            uid: null,
                            photoUrl: null,
                            ...item,
                            type: "user",
                        };
                }
            });
        users = _.flatten(users);
        users = _.uniqBy(users, "email");
        return users;
    };

    context.getSelectionWithRedundanciesRemoved = () => {
        const { selection } = context;

        const accessGroups = selection.filter(item => ["folder", "group"].includes(item.type) && !item.inviteError);
        let users = selection
            .filter(item => !item.inviteError && !["folder", "group"].includes(item.type) && (includeCurrentUser || !isCurrentUserItem(item)))
            .map(item => {
                switch (item.type) {
                    case "user":
                        return item;
                    case "email":
                    default:
                        return {
                            id: null,
                            uid: null,
                            photoUrl: null,
                            ...item,
                            type: "user",
                        };
                }
            });
        users = _.uniqBy(users, "email");

        // Remove users that are already included in the folders,
        //   but keeps those that already have permissions
        users = users.filter(user => (
            !!user.permissionType ||
            !accessGroups.some(accessGroup =>
                accessGroup.users.some(x => (
                    x.id === user.id ||
                    x.email === user.email
                ))
            )
        ));

        return [
            ...accessGroups,
            ...users,
        ];
    };

    const unsubscribeFromPresentationPermissionsController = PresentationPermissionsController.onStateDidUpdate((prevState, currentState) => {
        if (
            !_.isEqual(prevState.invites, currentState.invites) ||
            !_.isEqual(prevState.permissions, currentState.permissions)
        ) {
            context.init();
        }
    });
    const unsubscribeFromWorkspaceController = WorkspaceController.onStateDidUpdate((prevState, currentState) => {
        if (!_.isEqual(prevState.users, currentState.users) || !_.isEqual(prevState.userGroups, currentState.userGroups) || !_.isEqual(prevState.resourceFolders, currentState.resourceFolders)) {
            context.init();
        }
    });

    context.destroy = () => {
        unsubscribeFromPresentationPermissionsController();
        unsubscribeFromWorkspaceController();
    };

    context.init();

    return context;
};
